import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Button } from '@common/Button';
import { ClearOutlined, PlayArrowOutlined, StopOutlined } from '@mui/icons-material';
import { AppDispatch, State, tabActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslate } from '@common/hooks/useTranslate';
import { runWorkflowHandler } from '@common/Node/NodeSidebar/utils';
import { useStopwatch } from 'react-timer-hook';
import { WorkflowExecutionType } from '@common/Node/NodeSidebar/NodeExecutionHeader';

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  bottom: 20px;
  transform: translateX(-50%);
  left: 50%;

  & .MuiButton-startIcon {
    margin-right: 4px;
  }
`;

interface Props {
  abortControllerRef: React.MutableRefObject<AbortController | null>;
}

export const CanvasWidget: React.FC<Props> = ({ abortControllerRef }) => {
  const dispatch = useDispatch<AppDispatch>();

  const { seconds, minutes, start, pause, reset } = useStopwatch({ autoStart: false });
  const { service_id, workflow_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useTranslate();

  const unstored = useSelector((state: State) => state.tabs.active.unstored);
  const trace = useSelector((state: State) => state.tabs.active.run);
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const selectedNode = useSelector((state: State) => state.tabs.active.selectedNode);
  const trigger = commands?.find((command) => command.config.trigger);

  const isInboundMessage = trigger?.config.command_id === 'message.received';
  const isModule = trigger?.config.command_id === 'workflow.module';
  const workflowType = isModule ? 'Module' : 'Workflow';

  const completionNodes = commands.filter((command) => command.config.command_id === 'llm.completion');
  const isMessageArray = Boolean(
    completionNodes.find(
      (node) => node.state?.message_array?.enabled && node?.state?.messages?.type !== 'message_expression'
    )
  );

  const getType = () => {
    if (isInboundMessage) return WorkflowExecutionType.InboundMessage;
    if (isModule) return WorkflowExecutionType.Module;
    return WorkflowExecutionType.Request;
  };

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (selectedNode) return null;

  const run = async () => {
    await runWorkflowHandler({
      service_id,
      workflow_id,
      translate,
      dispatch,
      workflowExecution: {
        type: getType(),
        abortControllerRef,
        timer: {
          minutes,
          seconds,
          start,
          pause,
          reset,
        },
      },
      isMessageArray,
      enqueueSnackbar,
    });
  };

  return (
    <Container>
      {trace?.running ? (
        <Button pill customColor='red' startIcon={<StopOutlined />}>
          {`Stop ${workflowType}`}
        </Button>
      ) : trace?.id ? (
        <Button
          pill
          disabled={unstored}
          customColor='yellow'
          startIcon={<ClearOutlined />}
          onClick={() => dispatch(tabActions.clearWorkflowRun())}
        >
          Clear Execution
        </Button>
      ) : (
        <Button pill customColor='primary' disabled={!trigger} startIcon={<PlayArrowOutlined />} onClick={run}>
          {`Run ${workflowType}`}
        </Button>
      )}
    </Container>
  );
};
